/**
 * Real-estate lead journey shown in the WhatsApp hub. Journey automations
 * send Meta templates (business-initiated). They are built phase by phase;
 * until then they show as planned, with their templates ready to submit.
 */

export type JourneyStageKey = 'enquiry' | 'followup' | 'visit' | 'afterVisit' | 'booking' | 'afterSale';

export const JOURNEY_STAGES: Array<{ key: JourneyStageKey; title: string }> = [
  { key: 'enquiry', title: 'New enquiry' },
  { key: 'followup', title: 'Follow-up' },
  { key: 'visit', title: 'Site visit' },
  { key: 'afterVisit', title: 'After visit' },
  { key: 'booking', title: 'Booking' },
  { key: 'afterSale', title: 'After sale' },
];

export interface JourneyAutomation {
  key: string;
  title: string;
  stage: JourneyStageKey | 'marketing';
  category: 'UTILITY' | 'MARKETING';
  /** Build phase; Phase 1 is the hub itself. */
  phase: 2 | 3 | 4 | 5;
  summary: string;
  steps: Array<{ title: string; detail: string }>;
  buttonReplies?: string;
  /** Labels and button outcomes when a journey sends one of several templates. */
  templateConditions?: Record<string, string>;
  templateButtonReplies?: Record<string, string>;
  templates: string[];
  /** Templates available to staff manually; these do not gate or trigger the journey. */
  manualTemplates?: string[];
  skipWhen: string[];
  /** An older automation already does this; it moves here after testing. */
  replacesExisting?: string;
  /** Old automation-rule triggers that can send the same thing (duplicate warning). */
  oldRuleTriggers?: string[];
  /** Conversation flows that answer this automation's button taps. */
  dependsOn?: string[];
  /** Admin tool shown in the detail panel. */
  tool?: 'constructionComposer' | 'possessionComposer' | 'documentsChecklist' | 'priceInDrawer' | 'demandDueDate' | 'launchComposer' | 'festivalComposer' | 'collectionComposer' | 'coldLeadInfo' | 'birthdayInfo';
}

/** Journey automations up to this phase are built and can be switched on. */
export const BUILT_PHASE = 5;

export const isJourneyBuilt = (a: JourneyAutomation) => a.phase <= BUILT_PHASE;

const COMMON_SKIPS = ['Contact opted out (STOP)', 'Auto-replies are Off, or Test mode and not a test number', 'Template not approved in that language'];
const MARKETING_SKIPS = [...COMMON_SKIPS, 'Quiet hours (9 PM to 9 AM)', 'Already got a marketing message in the last 3 days', 'Lead is closed, lost, junk or booked'];

export const JOURNEY_AUTOMATIONS: JourneyAutomation[] = [
  {
    key: 'enquiryWelcome',
    title: 'Enquiry welcome',
    stage: 'enquiry',
    category: 'UTILITY',
    phase: 3,
    summary: 'A new lead receives one approved WhatsApp template, even if they have not messaged us. The project-specific welcome is used when CRM knows the project; otherwise the template asks them to select one.',
    steps: [
      { title: 'New lead created → approved welcome template', detail: 'No incoming WhatsApp message is required. Known project → brochure / visit / explore; unknown project → select one first. Conversation replies begin only after the customer taps or writes.' },
    ],
    templates: ['swarna_enquiry_received', 'swarna_enquiry_choose_project'],
    templateConditions: {
      swarna_enquiry_received: 'IF CRM KNOWS THE PROJECT',
      swarna_enquiry_choose_project: 'IF THE PROJECT IS UNKNOWN',
    },
    templateButtonReplies: {
      swarna_enquiry_received: 'Send brochure → that project’s brochure · Book site visit → visit slots for that project · Explore projects → project list with possession timelines',
      swarna_enquiry_choose_project: 'Select project → choose a project, see its possession timeline and next options',
    },
    skipWhen: [
      ...COMMON_SKIPS,
      'Lead already chatted on WhatsApp in the last 24 hours (the auto-reply covers it)',
      'Imported leads, and leads created from a WhatsApp chat',
      'Lead source is not Meta form (Social Ad), Website, Google or a property portal: walk-in, reference, call, C2C, manual',
      'Broker-referred leads (a broker is attached)',
      'Same number already got a welcome in the last 30 days',
      'Leads created before this automation was switched on',
    ],
    oldRuleTriggers: ['lead-created'],
    dependsOn: ['projectExplore', 'brochure', 'siteVisit', 'callback'],
  },
  {
    key: 'missedCall',
    title: 'Call not answered',
    stage: 'enquiry',
    category: 'UTILITY',
    phase: 3,
    summary: 'When an executive logs a call as CNR or busy on an open lead, follow up on WhatsApp so the customer can pick a time or chat instead. Max 3 per lead, at least 3 days apart.',
    steps: [{ title: 'Call logged as CNR / busy → follow-up', detail: 'swarna_call_attempt · Call me now / Call me later / Chat on WhatsApp' }],
    buttonReplies: 'Call me now → follow-up due now, chat flagged · Call me later → time picker, call-back follow-up · Chat on WhatsApp → chat flagged for the team',
    templates: ['swarna_call_attempt'],
    skipWhen: [
      ...COMMON_SKIPS,
      'Lead is lost, closed, junk or booked',
      'Already sent 3 times to this lead, or sent in the last 3 days',
      'Customer replied to an earlier one, or is chatting on WhatsApp (last 24 hours)',
      'Any automated message went to this number in the last 3 hours',
      'Call was logged more than 2 hours after it happened (calls logged after 9 PM wait until 10 AM)',
      'A wrong-number call stops all automated WhatsApp to that number',
    ],
    dependsOn: ['callback'],
  },
  {
    key: 'noReplyNudge',
    title: 'No-reply nudge',
    stage: 'followup',
    category: 'MARKETING',
    phase: 3,
    summary: 'Only after an Enquiry welcome was sent: one nudge 24 hours later if the customer has not replied and nobody was in touch. Nothing more is automated after it. Marketing: sent 9 AM to 9 PM only.',
    steps: [
      { title: 'No reply and no contact in 24 h → one nudge', detail: 'swarna_enquiry_nudge · homes, area and possession from Website CMS · Book site visit / Send brochure / Stop updates (no price: points to a site visit)' },
      { title: 'Still nothing after 24 h → call task', detail: 'Follow-up due now, plus a "No reply in 2 days: call" item in My Today and the Today page (cleared by Done or a logged call)' },
    ],
    buttonReplies: 'Book site visit → slot list · Send brochure → brochure PDF · Stop updates → contact opted out',
    templates: ['swarna_enquiry_nudge'],
    skipWhen: [
      ...MARKETING_SKIPS.filter((x) => !x.startsWith('Lead is closed')),
      'No Enquiry welcome was sent (walk-in, broker, manual leads; already chatting; welcomed in the last 30 days)',
      'Customer replied on WhatsApp, any call or visit was logged, checked in at the front desk, or the lead moved past New',
      'Another automated WhatsApp went out in the last 24 hours (e.g. "we tried to call you")',
      'Leads created before this automation was switched on',
    ],
    dependsOn: ['siteVisit', 'brochure'],
  },
  {
    key: 'coldLead',
    title: 'Cold lead check-in',
    stage: 'followup',
    category: 'MARKETING',
    phase: 5,
    summary: 'Soft check-in for open leads quiet for 7 to 30 days. Runs daily at 11 AM, up to 50 leads a day, once per lead per month.',
    steps: [{ title: 'No activity for 7 to 30 days → check-in at 11 AM', detail: 'swarna_cold_lead · Share prices / Book site visit / Stop updates' }],
    buttonReplies: 'Share prices → brochure with prices · Book site visit → slot list · Stop updates → opted out',
    templates: ['swarna_cold_lead'],
    skipWhen: [...MARKETING_SKIPS, 'Already checked in during the last 30 days', 'Quiet for more than 30 days (too old to nudge)'],
    dependsOn: ['brochure', 'siteVisit'],
    tool: 'coldLeadInfo',
  },
  {
    key: 'visitConfirm',
    title: 'Visit confirmation',
    stage: 'visit',
    category: 'UTILITY',
    phase: 2,
    summary: 'Sent as soon as a site visit is scheduled in the lead drawer.',
    steps: [{ title: 'Visit scheduled → confirmation', detail: 'swarna_visit_confirmed · Reschedule / Cancel visit' }],
    buttonReplies: 'Reschedule → slot list, appointment moved · Cancel visit → appointment cancelled, task for the executive',
    templates: ['swarna_visit_confirmed'],
    skipWhen: [...COMMON_SKIPS, 'Visit was scheduled before this automation was switched on'],
    oldRuleTriggers: ['appointment-scheduled'],
  },
  {
    key: 'visitReminders',
    title: 'Visit reminders',
    stage: 'visit',
    category: 'UTILITY',
    phase: 2,
    summary: 'Reminder the day before and 2 hours before, the second one with the site pin.',
    steps: [
      { title: 'Day before, 6 PM → reminder', detail: "swarna_visit_reminder_day · I'll be there / Reschedule (skipped for visits less than 3 hours away)" },
      { title: '2 hours before → reminder with map', detail: 'swarna_visit_reminder_soon · Running late / Reschedule' },
    ],
    buttonReplies: "I'll be there → visit marked confirmed · Running late → executive alerted · Reschedule → slot list",
    templates: ['swarna_visit_reminder_day', 'swarna_visit_reminder_soon'],
    skipWhen: [
      ...COMMON_SKIPS,
      'Visit cancelled or already completed',
      'Confirmation went out less than 6 hours (day reminder) or 90 minutes (2-hour reminder) earlier',
      'Day reminder: the visit was scheduled or moved on the same day (that day\'s confirmation is enough)',
      '2-hour reminder: before 8 AM',
      '2-hour reminder: the project has no site latitude/longitude in Website CMS',
    ],
    oldRuleTriggers: ['appointment-scheduled'],
  },
  {
    key: 'visitNoShow',
    title: 'Missed visit',
    stage: 'visit',
    category: 'UTILITY',
    phase: 2,
    summary: 'Only after the team marks "Didn\'t come" (never from the clock): offer a new time. Not sent after a 2nd no-show on the same lead.',
    steps: [
      { title: 'Team marks "Didn\'t come" → new time', detail: 'swarna_visit_missed · Pick a new time / Call me · plus a call task for 10 AM next day' },
    ],
    buttonReplies: 'Pick a new time → slot list, appointment moved, confirmation sent · Call me → follow-up due now, chat flagged for the team',
    templates: ['swarna_visit_missed'],
    skipWhen: [...COMMON_SKIPS, 'Visit was marked done or cancelled'],
  },
  {
    key: 'visitBrochure',
    title: 'Visit brochure',
    stage: 'afterVisit',
    category: 'UTILITY',
    phase: 4,
    summary: 'When a visit is completed, send the project brochure PDF and details link in the customer’s language when approved. Once per lead per visit day.',
    steps: [{ title: 'Visit completed → brochure and project details', detail: 't2_site_visit_brochure · Attaches the project brochure PDF and opens the project details page' }],
    templates: ['t2_site_visit_brochure'],
    skipWhen: [...COMMON_SKIPS, 'Lead closed', 'Project brochure or website slug missing', 'Already sent to this lead today', 'Visit before this automation was switched on'],
    oldRuleTriggers: ['visit-completed'],
  },
  {
    key: 'visitThanks',
    title: 'Visit feedback',
    stage: 'afterVisit',
    category: 'UTILITY',
    phase: 4,
    summary: 'About 10 minutes after the presenter saves the visit (walk-in or scheduled): how was our service. Never changes the lead\'s rating or status.',
    steps: [{ title: 'Visit saved → service question in ~10 min', detail: 'swarna_visit_feedback · Excellent / Good / Could be better (saved 9 PM–8 AM: 10 AM)' }],
    buttonReplies: 'Excellent / Good → thank you · Could be better → asks what to improve; admins and sales managers are alerted. Scores show on the Today page.',
    templates: ['swarna_visit_feedback'],
    skipWhen: [...COMMON_SKIPS, 'Lead closed', 'Visit saved on a later day than the visit', 'Already sent to this lead today', 'Visit before this automation was switched on'],
  },
  {
    key: 'visitFollowup',
    title: 'Visit follow-up',
    stage: 'afterVisit',
    category: 'UTILITY',
    phase: 4,
    summary: 'Next day 11 AM after a visit: any questions? Free in the chat if the customer replied in the last 24 hours, otherwise a template.',
    steps: [{ title: 'Next day 11 AM → follow-up', detail: 'swarna_visit_followup · Need more details / Not for me' }],
    buttonReplies: 'Need more details → Call me now / Call me later (morning, afternoon, evening) / Schedule a visit · Not for me → asks why, follow-up due now for review',
    templates: ['swarna_visit_followup'],
    skipWhen: [...COMMON_SKIPS, 'Lead closed', 'The team called after the visit', 'Another visit is scheduled', 'Customer wrote to us after the visit', 'Visit before this automation was switched on'],
    dependsOn: ['callback', 'siteVisit'],
  },
  {
    key: 'priceDetails',
    title: 'Unit details',
    stage: 'afterVisit',
    category: 'UTILITY',
    phase: 4,
    summary: 'Executive sends the unit discussed, from the lead drawer (tag icon next to the phone number). Never a price: the message invites them to see pricing at a site visit.',
    steps: [{ title: 'Executive enters the unit → details', detail: 'swarna_unit_details · Schedule a visit / Call me' }],
    buttonReplies: 'Schedule a visit → slot list · Call me → time picker, call-back follow-up',
    templates: ['swarna_unit_details'],
    skipWhen: COMMON_SKIPS,
    dependsOn: ['siteVisit', 'callback'],
    tool: 'priceInDrawer',
  },
  {
    key: 'bookingConfirmed',
    title: 'Booking confirmed',
    stage: 'booking',
    category: 'UTILITY',
    phase: 4,
    summary: 'Congratulations to the buyer when the first customer is added to a booking.',
    steps: [{ title: 'First customer added → confirmation after 30 min', detail: 'swarna_booking_welcome (waits 30 minutes so the booking receipt is usually in)' }],
    templates: ['swarna_booking_welcome'],
    skipWhen: [...COMMON_SKIPS, 'Booking made before this automation was switched on', 'Customer has no mobile number'],
  },
  {
    key: 'bookingDocuments',
    title: 'Documents checklist',
    stage: 'booking',
    category: 'UTILITY',
    phase: 4,
    summary: 'Documents needed for the agreement, with one reminder after 3 days if KYC (PAN + Aadhaar for every applicant) is still missing.',
    steps: [
      { title: 'With the booking confirmation → checklist', detail: 'swarna_booking_documents · I have a question' },
      { title: 'KYC still missing after 3 days → reminder', detail: 'Same template' },
    ],
    buttonReplies: 'I have a question → chat flagged for the team',
    templates: ['swarna_booking_documents'],
    skipWhen: [...COMMON_SKIPS, 'PAN and Aadhaar already uploaded for every applicant'],
    tool: 'documentsChecklist',
  },
  {
    key: 'paymentDue',
    title: 'Payment due',
    stage: 'afterSale',
    category: 'UTILITY',
    phase: 4,
    summary: 'Reminders for a demand letter that has a due date: 7 days and 1 day before, at 10 AM.',
    steps: [
      { title: 'Demand letter with due date → reminder 7 days before', detail: 'swarna_payment_due · Already paid / Talk to accounts (sent right away if the due date is closer)' },
      { title: '1 day before → reminder', detail: 'Same template' },
    ],
    buttonReplies: 'Already paid → flagged for accounts to verify · Talk to accounts → flagged for accounts',
    templates: ['swarna_payment_due'],
    skipWhen: [...COMMON_SKIPS, 'A customer receipt was recorded after the demand', 'Demand letter has no due date', 'Demand created before this automation was switched on'],
    tool: 'demandDueDate',
  },
  {
    key: 'paymentReceived',
    title: 'Payment received',
    stage: 'afterSale',
    category: 'UTILITY',
    phase: 4,
    summary: 'Automatic confirmation when a property or GST payment is recorded, plus a payment-details PDF template staff can send manually from Office.',
    steps: [
      { title: 'Property payment recorded → confirmation', detail: 'swarna_property_payment_receipt' },
      { title: 'GST payment recorded → confirmation', detail: 'swarna_gst_payment_receipt' },
    ],
    templates: ['swarna_property_payment_receipt', 'swarna_gst_payment_receipt'],
    manualTemplates: ['swarna_unit_payment_details'],
    skipWhen: [...COMMON_SKIPS, 'Receipt recorded before this automation was switched on'],
    replacesExisting: 'Payment receipt WhatsApp automation',
    oldRuleTriggers: ['customer-payment-receipt'],
  },
  {
    key: 'constructionUpdate',
    title: 'Construction update',
    stage: 'afterSale',
    category: 'UTILITY',
    phase: 4,
    summary: 'Admin sends a progress update to every buyer of a project (units booked or sold).',
    steps: [{ title: 'Admin writes the update → all buyers', detail: 'swarna_construction_update' }],
    templates: ['swarna_construction_update'],
    skipWhen: [...COMMON_SKIPS, 'Buyer has no mobile number'],
    tool: 'constructionComposer',
  },
  {
    key: 'possession',
    title: 'Possession ready',
    stage: 'afterSale',
    category: 'UTILITY',
    phase: 4,
    summary: 'Admin announces that a project is ready for possession, to every buyer, with a button to schedule the handover.',
    steps: [{ title: 'Admin sets the date → all buyers', detail: 'swarna_possession_ready · Schedule handover / Call me' }],
    buttonReplies: 'Schedule handover / Call me → chat flagged for the team (Contacts → Asked for the team)',
    templates: ['swarna_possession_ready'],
    skipWhen: [...COMMON_SKIPS, 'Buyer has no mobile number'],
    tool: 'possessionComposer',
  },
  {
    key: 'newLaunch',
    title: 'New launch',
    stage: 'marketing',
    category: 'MARKETING',
    phase: 5,
    summary: 'Launch announcement to the audience you choose: open leads, leads of one project, buyers, or buyers and leads. Send now or schedule for 10 AM on a date.',
    steps: [{ title: 'Admin writes the launch → chosen audience', detail: 'swarna_new_launch · Send details / Book site visit / Stop updates (up to 1,000 per send)' }],
    buttonReplies: 'Send details → brochure of the chosen website project · Book site visit → slot list · Stop updates → opted out',
    templates: ['swarna_new_launch'],
    skipWhen: MARKETING_SKIPS,
    dependsOn: ['brochure', 'siteVisit'],
    tool: 'launchComposer',
  },
  {
    key: 'birthday',
    title: 'Birthday wish',
    stage: 'marketing',
    category: 'MARKETING',
    phase: 5,
    summary: 'Birthday wish to buyers and brokers at 10 AM, using the date of birth saved on them. Once a year per number.',
    steps: [{ title: 'Birthday today → wish at 10 AM', detail: 'swarna_birthday · Stop updates' }],
    templates: ['swarna_birthday'],
    skipWhen: MARKETING_SKIPS.filter((x) => !x.startsWith('Lead is closed')),
    replacesExisting: 'Birthday wishes',
    oldRuleTriggers: ['old-birthday'],
    tool: 'birthdayInfo',
  },
  {
    key: 'festival',
    title: 'Festival greeting',
    stage: 'marketing',
    category: 'MARKETING',
    phase: 5,
    summary: 'Festival greeting to the audience you choose. Schedule it ahead for 10 AM on the festival day.',
    steps: [{ title: 'Admin schedules the greeting → chosen audience at 10 AM', detail: 'swarna_festival_greeting · Stop updates' }],
    templates: ['swarna_festival_greeting'],
    skipWhen: MARKETING_SKIPS,
    tool: 'festivalComposer',
  },
  {
    key: 'collectionReady',
    title: 'Ready for collection',
    stage: 'marketing',
    category: 'MARKETING',
    phase: 5,
    summary: 'Send a pickup notice to selected people for a gift or another item ready for collection.',
    steps: [{ title: 'Admin selects recipients and pickup details → send or schedule', detail: 'swarna_collection_ready · Get directions / Stop updates' }],
    templates: ['swarna_collection_ready'],
    skipWhen: MARKETING_SKIPS.filter((x) => !x.startsWith('Lead is closed')),
    tool: 'collectionComposer',
  },
];
